import { API } from "@/lib/api";
import { isMuted } from "@/lib/sounds";
import { currentLang } from "@/i18n";

// Auto-read preference only (non-sensitive) — localStorage is appropriate.
const AUTO_KEY = "huey-auto-read";
export const isAutoRead = () => localStorage.getItem(AUTO_KEY) === "1";
export const setAutoRead = (v) => localStorage.setItem(AUTO_KEY, v ? "1" : "0");

// One voice at a time: { id, status: "loading" | "playing", audio?, synth? }
let current = null;
const listeners = new Set();
export const onVoiceChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const emit = () => listeners.forEach((fn) => fn(current ? { id: current.id, status: current.status } : null));

export const stopHuey = () => {
  if (current?.audio) { current.audio.pause(); current.audio.src = ""; }
  if (current?.synth) window.speechSynthesis?.cancel();
  const was = current;
  current = null;
  if (was) emit();
};

const fallbackSpeak = (id, text) => {
  if (!("speechSynthesis" in window) || !text) return false;
  const u = new SpeechSynthesisUtterance(text);
  u.lang = currentLang();
  u.rate = 0.95;
  u.pitch = 1.1;
  u.onend = u.onerror = () => { if (current?.id === id) { current = null; emit(); } };
  current = { id, status: "playing", synth: true };
  emit();
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(u);
  return true;
};

// Fetches an mp3 from the backend and plays it; browser voice as fallback. Returns false when muted/unavailable.
export const playVoice = async (id, request, fallbackText) => {
  if (isMuted()) return false;
  stopHuey();
  const audio = new Audio();
  current = { id, status: "loading", audio };
  emit();
  try {
    const res = await fetch(request.url, { method: request.method || "GET", credentials: "include", headers: request.body ? { "Content-Type": "application/json" } : undefined, body: request.body ? JSON.stringify(request.body) : undefined });
    if (!res.ok) throw new Error(`tts ${res.status}`);
    if (current?.id !== id) return false;
    audio.src = URL.createObjectURL(await res.blob());
    audio.onended = audio.onerror = () => { URL.revokeObjectURL(audio.src); if (current?.id === id) { current = null; emit(); } };
    await audio.play();
    if (current?.id === id) { current.status = "playing"; emit(); }
    return true;
  } catch (e) {
    console.warn("Voice fallback:", e.message);
    if (current?.id !== id) return false;
    return fallbackSpeak(id, fallbackText);
  }
};

export const hueySpeak = (id, text) => (text ? playVoice(id, { url: `${API}/huey/speak`, method: "POST", body: { text: text.slice(0, 1200) } }, text) : false);

export const voiceState = () => (current ? { id: current.id, status: current.status } : null);
